import { type ReactElement } from "react";
import { useGetList } from "react-admin";
import { FormProvider, useForm } from "react-hook-form";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { SelectInput } from "../../inputs/SelectInput";
import { type CreateCalendarEventDto, serverAPI } from "../../api/server.api";
import { Branch } from "../../providers/branchesProvider";
import { InstructorInput } from "../../inputs/InstructorInput";
import { Form2Theme } from "../../misc/Form2";
import { WhenFormIsInitialized } from "../../misc/WhenFormIsInitialized";
import { TheoryLessonAttendanceState } from "../../model/autovioCalendarEvents";
import { Student } from "../../providers/studentsProvider";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { TheoryLecture, TheoryLectureAbbreviation, theoryLectureFromAbbreviation } from "../../model/TheoryLecture";
import { TheoryLectureInput } from "../../inputs/TheoryLectureInput";

export function CreateTheoryLessonForm(props: {
  courseId?: string;
  initialDateTime?: DateTime;
  initialLecture?: TheoryLectureAbbreviation;
  isHistoricalLesson?: boolean;
  branchId?: string;
  instructorId?: string;
  bookedTrainingId?: string;
  student?: Student;
  backButton?: ReactElement;
  onSuccess: () => void;
}) {
  const {
    courseId,
    initialDateTime,
    initialLecture,
    isHistoricalLesson = false,
    bookedTrainingId,
    branchId,
    instructorId,
    student,
    backButton,
    onSuccess,
  } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { data: branches = [] } = useGetList<Branch>("branches", { filter: { drivingSchoolId } });

  const defaultValues = {
    courseId: courseId ?? "",
    lecture: initialLecture ?? "",
    start: initialDateTime ?? undefined,
    ...(isHistoricalLesson && {
      branchId,
      instructorId,
      attendance: "attended",
      bookedTrainingId,
    }),
  } satisfies TheoryLessonFormValues;

  const formProps = useForm<TheoryLessonFormValues>({ defaultValues });

  function validate(formValues: TheoryLessonFormValues): TheoryLessonParams {
    const { courseId, lecture, start, branchId, instructorId, bookedTrainingId } = formValues;
    if (!lecture) {
      throw new Error(`Invalid lecture: ${JSON.stringify(lecture)}`);
    }
    if (!start) {
      throw new Error(`Invalid start time: ${start}`);
    }
    if (!branchId) {
      throw new Error(`Invalid branchId: ${branchId}`);
    }
    if (!instructorId) {
      throw new Error(`Invalid instructorId: ${instructorId}`);
    }
    if (isHistoricalLesson) {
      if (!student) {
        throw new Error(`Invalid student: ${student}`);
      }
      if (!bookedTrainingId) {
        throw new Error(`Invalid bookedTrainingId: ${bookedTrainingId}`);
      }
    }
    return {
      courseId,
      theoryLecture: theoryLectureFromAbbreviation(lecture),
      start,
      branchId,
      instructorId,
      ...(isHistoricalLesson && {
        bookedTrainingId,
        attendance: "attended",
      }),
    } satisfies TheoryLessonParams;
  }

  const onSubmit = async (formValues: TheoryLessonFormValues) =>
    runSafely(
      async () => {
        const { courseId, theoryLecture, start, attendance, bookedTrainingId, ...params } = validate(formValues);
        const payload: CreateCalendarEventDto = {
          type: "TheoryLesson" as const,
          uid: uuidv4(),
          theoryLecture,
          start: start.toISO(),
          end: start.plus({ minutes: theoryLecture.category === "B96" ? 150 : 90 }).toISO(),
          courseUid: courseId || uuidv4(),
          ...(isHistoricalLesson &&
            student &&
            bookedTrainingId &&
            attendance && {
              students: [
                {
                  id: student.id,
                  bookedTrainingIds: [bookedTrainingId],
                  rsvp: attendance,
                },
              ],
            }),
          ...params,
        };
        await serverAPI.createCalendarEvent(payload);
      },
      {
        errorMessage: "Fehler beim Anlegen der Theoriestunde.",
        successMessage: "Theoriestunde erfolgreich angelegt.",
        onSuccess,
      },
    );

  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <form onSubmit={formProps.handleSubmit(onSubmit)} style={{ height: "100%" }}>
          <Column height="100%" minHeight={500} gap="1em">
            <WhenFormIsInitialized>
              <TheoryLectureInput
                label="Lektion"
                source="lecture"
                validate={(value) => (value ? true : "Bitte wähle die Lektion")}
                size="small"
              />
              <DateTimeInput size="small" source="start" allowPastDates allowFutureDates={!isHistoricalLesson} />
              <SelectInput
                label="Filiale"
                source="branchId"
                size="small"
                options={branches.map((branch) => [branch.id, branch.name])}
                validate={(value) => (value ? true : "Bitte wähle die Filiale aus.")}
              />
              <InstructorInput source="instructorId" branchId={formProps.watch("branchId")} searchable size="small" />
              {isHistoricalLesson && (student?.bookedTrainings ?? []).length > 1 && (
                <SelectInput
                  label="Ausbildung"
                  source="bookedTrainingId"
                  size="small"
                  options={(student?.bookedTrainings ?? []).map((bt) => [bt.id, bt.drivingLicenseClass])}
                  validate={(value) => (value ? true : "Bitte wähle die Ausbildung")}
                />
              )}
              <Row sx={{ justifyContent: "space-between", mt: "auto" }}>
                {backButton ?? <div />}
                <SaveButton2 />
              </Row>
            </WhenFormIsInitialized>
          </Column>
        </form>
      </FormProvider>
    </Form2Theme>
  );
}

export interface TheoryLessonFormValues {
  courseId: string;
  lecture: "" | TheoryLectureAbbreviation;
  start?: DateTime;
  branchId?: string;
  instructorId?: string;
  bookedTrainingId?: string;
}

interface TheoryLessonParams {
  courseId: string;
  theoryLecture: TheoryLecture;
  start: DateTime;
  branchId: string;
  instructorId: string;
  bookedTrainingId?: string;
  attendance?: TheoryLessonAttendanceState;
}
